import { useCallback } from "react";
import Button from "components/shared/button";
import Input from "components/shared/inputs/input";
import { Popup } from "components/popups/Popup";
import useCustomForm from "utils/hooks/useCustomForm";
import useTranslation from "utils/hooks/useTranslation";
import { useUpdateProjectMutation, useLazyLoadProjectQuery } from "store/api";
import { useAppDispatch } from "store/hooks";
import { addNotification } from "store/componentsSlice";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

export const RenameProjectPopup: React.FC<{
    item: any;
    onClose: () => void;
}> = ({ item, onClose }) => {
    const { t } = useTranslation();
    const [updateProject] = useUpdateProjectMutation();
    const [loadProject] = useLazyLoadProjectQuery();
    const dispatch = useAppDispatch();

    const schema = yup.object({
        projectName: yup.string().required(),
    });

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useCustomForm({
        defaultValues: {
            projectName: item?._name || "",
        },
        resolver: yupResolver(schema),
    });

    const onSubmit = useCallback(async (formData: any) => {
        try {
            // Load full project data first to ensure we don't lose other header info
            const projectInfo = await loadProject({ id: item.id, uid: item._uid }).unwrap();

            const updatedProject = {
                ...projectInfo,
                id: item.id, // Ensure ID is present
                data: {
                    ...projectInfo.data,
                    header: {
                        ...projectInfo.data?.header,
                        projectName: formData.projectName,
                    },
                },
            };

            await updateProject(updatedProject).unwrap();

            dispatch(addNotification({
                type: 'success',
                message: t("PROJECT_RENAMED"),
            }));
            onClose();
        } catch (err) {
            console.error(err);
            dispatch(addNotification({
                type: 'error',
                message: t("ERROR_RENAME"),
            }));
        }
    }, [item, loadProject, updateProject, dispatch, t, onClose]);

    return (
        <Popup title={t("RESULTS_RENAME")} translateTitle onClose={onClose} fixSize="small">
            <group data-direction="column" data-gap="20" data-space="20">
                <Input
                    // label={"PROJECT_NAME"}
                    control={control}
                    name="projectName"
                    type="text"
                    error={errors.projectName}
                    autoFocus
                    maxLength={20}
                />
                <group data-justify="end" data-gap="10">
                    <Button secondary large onClick={handleSubmit(onSubmit)}>
                        {t("NAV_SAVE")}
                    </Button>
                    <Button accent secondary large onClick={onClose}>
                        {t("CANCEL")}
                    </Button>
                </group>
            </group>
        </Popup>
    );
};
